import { useImmer } from "use-immer";
import { useEffect } from "react";
import DayOfWeekCheckboxes from "../DayOfWeekCheckboxes";
import Multiselect from "../Multiselect";
import FrontendConstant from "../../constant/FrontendConstant";

const isGeneticMode = (algorithm) => algorithm === "GENETIC";

const Component = ({ config, ruleInfoItems, onChange }) => {
  const [data, updateData] = useImmer(config);

  useEffect(() => {
    updateData(config);
  }, [config]);

  useEffect(() => {
    onChange(data);
  }, [data]);

  const algorithm = data.defaultSearchAlgorithm || "RANDOM";

  const handleAlgorithmChange = (newAlgorithm) => {
    updateData((d) => {
      d.defaultSearchAlgorithm = newAlgorithm;
      // Reset GA-specific fields when switching away from genetic modes
      if (!isGeneticMode(newAlgorithm)) {
        d.gaPopulationSize = null;
        d.gaMaxGenerations = null;
      }
    });
  };

  return (
    <article>
      <table className="config-table">
        <thead>
          <tr>
            <th>Attribute</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Update on days</td>
            <td>
              <DayOfWeekCheckboxes
                dayOfWeekArray={data.updateAtDays}
                onChange={(e) =>
                  updateData((d) => {
                    d.updateAtDays = e;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>Update on time [CET]</td>
            <td>
              <input
                type="time"
                value={data.updateAtTime}
                onChange={(e) =>
                  updateData((d) => {
                    d.updateAtTime = e.target.value;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>History lookup [days]</td>
            <td>
              <input
                type="number"
                value={data.historyLookupDaysMin}
                onChange={(e) =>
                  updateData((d) => {
                    d.historyLookupDaysMin =
                      e.target.value < 0 ? e.target.value : 0;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>History lookup [days]</td>
            <td>
              <input
                type="number"
                value={data.historyLookupDaysMax}
                onChange={(e) =>
                  updateData((d) => {
                    d.historyLookupDaysMax =
                      e.target.value < 0 ? e.target.value : 0;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>False positives quotient max.</td>
            <td>
              <input
                type="number"
                value={data.falsePositivesPercentageMax}
                step={0.01}
                onChange={(e) =>
                  updateData((d) => {
                    d.falsePositivesPercentageMax =
                      e.target.value > 0 ? e.target.value : 0;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>True positives quotient min.</td>
            <td>
              <input
                type="number"
                value={data.truePositivesPercentageMin}
                step={0.01}
                onChange={(e) =>
                  updateData((d) => {
                    d.truePositivesPercentageMin =
                      e.target.value > 0 ? e.target.value : 0;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>Threshold</td>
            <td>
              <input
                type="number"
                value={data.threshold}
                step={0.01}
                onChange={(e) =>
                  updateData((d) => {
                    d.threshold = e.target.value > 2.0 ? e.target.value : 2.0;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>Weights found max.</td>
            <td>
              <input
                type="number"
                value={data.weightsFoundMax}
                step={1}
                onChange={(e) =>
                  updateData((d) => {
                    d.weightsFoundMax =
                      e.target.value > 2.0 ? e.target.value : 2.0;
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td>Ignored rules</td>
            <td>
              <Multiselect
                values={ruleInfoItems.map((rii) => rii.ruleScoreColumnName)}
                selectedValues={data.ignoredRules}
                onChange={(e) =>
                  updateData((d) => {
                    d.ignoredRules = e;
                  })
                }
              ></Multiselect>
            </td>
          </tr>
          <tr>
            <td>Use manual weights</td>
            <td>
              <label
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={!!data.useManualWeights}
                  onChange={(e) =>
                    updateData((d) => {
                      d.useManualWeights = e.target.checked;
                    })
                  }
                />
                {data.useManualWeights ? "Yes" : "No"}
              </label>
            </td>
          </tr>
          <tr>
            <td>
              Default search algorithm
              <span
                className="info-hint"
                data-hint="Algorithm used by scheduled (automatic) simulation runs. RANDOM is the well performing default."
              >
                ⓘ
              </span>
            </td>
            <td>
              <select
                value={algorithm}
                onChange={(e) => handleAlgorithmChange(e.target.value)}
              >
                {FrontendConstant.searchAlgorithms.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </td>
          </tr>
          {isGeneticMode(algorithm) && (
            <>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    color: "orange",
                    fontSize: "0.85em",
                    padding: "6px 8px",
                  }}
                >
                  ⚠ When using the GENETIC algorithm type, you also need to
                  update the "Weights auto update" -> "Total job iterations min." value
                  since GENETIC runs typically complete in around 15 million iterations.
                </td>
              </tr>
              <tr>
                <td>
                  GA population size
                  <span
                    className="info-hint"
                    data-hint="Number of candidate weight sets evolved per generation. Default: 200. Larger = more diversity, slower per generation."
                  >
                    ⓘ
                  </span>
                </td>
                <td>
                  <input
                    type="number"
                    value={data.gaPopulationSize || 200}
                    min={10}
                    max={2000}
                    onChange={(e) =>
                      updateData((d) => {
                        d.gaPopulationSize = parseInt(e.target.value, 10);
                      })
                    }
                  />
                </td>
              </tr>
              <tr>
                <td>
                  GA max generations
                  <span
                    className="info-hint"
                    data-hint="Hard cap on evolution generations. Leave empty to run until the time limit."
                  >
                    ⓘ
                  </span>
                </td>
                <td>
                  <input
                    type="number"
                    value={data.gaMaxGenerations || ""}
                    placeholder="unlimited"
                    min={1}
                    onChange={(e) =>
                      updateData((d) => {
                        d.gaMaxGenerations = e.target.value
                          ? parseInt(e.target.value, 10)
                          : null;
                      })
                    }
                  />
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </article>
  );
};

export default Component;
